<script setup lang="ts">
//#region imports
import { computed, nextTick, ref } from 'vue';
import hash from 'object-hash';
import { useApi } from '@/api';
import { useCrmStatus } from '@/views/CRM/composables/useCrmStatus';
import { useSegmentRules } from '@/views/CRM/composables/useSegmentRules';
import { useLodash } from '@/setup/lodash';
import CrmTag from '@/views/CRM/components/CrmTag.vue';
import SegmentInfo from '@/views/CRM/Segments/SegmentInfo.vue';
import { saveBlob } from '@/utils/fileSaver';
import FilterComponent from '@/views/CRM/components/FilterComponent.vue';
import Comments from '@/components/Comments.vue';
import { Tree, TreeBranch } from '@/views/CRM/components/Tree';
import { segmentFilters } from '@/views/CRM/constants/segmentFilters';
import { usePermissions } from '@/composables/usePermissions';
import type {
  CrmActivity,
  CrmSegment,
  CrmSegmentByIdStatsFilters,
  CrmSegmentRuleFilters,
  CrmSegmentRuleGroup,
  CrmSegmentStats,
  CrmSegmentRuleFilter,
} from '@/types/crm';
import type { EmitsToListeners } from '@/types/core';
import type { GetCrmSegmentsByIdResponses, PutCommentsByIdData } from '@/types/openapi-ts';
//#endregion

//#region types
interface Props {
  playerAttached?: boolean;
  segmentId: string;
  currentBrand: string;
  showFiltersMode?: 'none' | 'preview' | 'edit';
  showEdit?: boolean;
  activity?: Partial<CrmActivity>;
  activityFilters?: CrmSegmentRuleFilters;
  isLocalSegment?: boolean;
  localSegment?: SegmentViewState | null;
  activitySegmentType?: 'global' | 'local' | '';
  state?: string;
}

interface Emits {
  updateFilterStats: [CrmSegmentStats];
  'update:activity-filters': [CrmSegmentRuleFilters];
  edit: [string];
  close: [];
}

export interface ViewSegmentParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>;
}

type SegmentViewStateBase = Omit<Partial<CrmSegment>, 'name' | 'type' | 'status' | 'moderation_status' | 'rules_group'>;
type SegmentViewState = SegmentViewStateBase & {
  name: string;
  type: 'fixed' | 'dynamic' | 'manual';
  status: string;
  moderation_status: GetCrmSegmentsByIdResponses[200]['payload']['moderation_status'] | '';
  rules_group: CrmSegmentRuleGroup;
};
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  playerAttached: false,
  showFiltersMode: 'none',
  showEdit: true,
  activity: () => ({}),
  activityFilters: () => [],
  isLocalSegment: false,
  localSegment: null,
  activitySegmentType: '',
  state: '',
});

const emit = defineEmits<Emits>();
//#endregion

//#region const
const statusIcon = {
  fixed: 'lock',
  dynamic: 'wave-square',
  manual: 'gear',
};

const defaultRulesGroup: CrmSegmentRuleGroup = {
  logical_relations: '',
  rules: [
    {
      logical_relations: '',
      rules: [
        {
          rule_type: '',
          operator: '',
          values: '',
        },
      ],
    },
  ],
};
//#endregion

//#region composables
const api = useApi();
const _ = useLodash();
const { detectCrmStatus } = useCrmStatus();
const { clearSegment } = useSegmentRules();
const { hasPermission } = usePermissions();
//#endregion

//#region state
const isVisible = ref(false);
const isLoading = ref(false);
const isSegmentInfoLoading = ref(false);
const isSaveLoading = ref(false);
const statsLoadingCount = ref(0);
const filterLoading = ref(0);

const stats = ref<CrmSegmentStats | null>(null);
const permissions = ref<any>({});
const segment = ref<SegmentViewState>({
  name: '',
  type: 'fixed',
  status: '',
  moderation_status: '',
  rules_group: defaultRulesGroup,
});

const additionalFilters = ref<CrmSegmentRuleFilters>([]);
const lastHash = ref('');
//#endregion

//#region computed
const availableFilters = computed(() => {
  if (hasPermission('affiliates_view')) {
    return segmentFilters;
  }

  return segmentFilters.map((group) => ({
    ...group,
    items: group.items.filter((item) => {
      return item.name !== 'player_affiliate_id' && item.name !== 'player_sub_affiliate_id';
    }),
  }));
});
const canReview = computed(() => permissions.value.can_review);
const canEdit = computed(() => permissions.value.can_edit);
const status = computed(() => detectCrmStatus(segment.value));
const isStatsLoading = computed(() => statsLoadingCount.value !== 0);
const isActive = computed(() => !isLoading.value && !isStatsLoading.value && !isSaveLoading.value);

const rulesGroup = computed(() => (
  !_.isEmpty(segment.value.rules_group) ? segment.value.rules_group : defaultRulesGroup
));
const segmentInfoModerationStatus = computed(() => {
  return !_.isEmpty(props.activity) ? props.activity.moderation_status || '' : segment.value.moderation_status;
});
const activitySegmentTypeValue = computed<'global' | ''>(() => (
  props.activitySegmentType === 'global' ? 'global' : ''
));
const showFiltersModeValue = computed<'none' | 'edit'>(() => (
  props.showFiltersMode === 'edit' ? 'edit' : 'none'
));

const calcPlayerAttached = computed(() => {
  if (!_.isEmpty(props.activity)) {
    return props.playerAttached;
  }

  if (!segment.value.is_players_attached) {
    getPlayers();
  }

  return segment.value.is_players_attached;
});

const usedInActivities = computed(() => {
  return segment.value.used_in_activities && segment.value.used_in_activities.length
    ? segment.value.used_in_activities.map((activity) => activity.name).join(', ')
    : '';
});
//#endregion

//#region methods
function getDefaultRule(): CrmSegmentRuleFilter {
  return {
    rule_type: '',
    operator: '',
    values: '',
  };
}

async function checkChanges(value: CrmSegmentRuleFilters = additionalFilters.value) {
  const checkFilter = clearFilter(value);
  const currentHash = hash(checkFilter, { algorithm: 'md5' });
  if (currentHash !== lastHash.value) {
    await getStats();
    lastHash.value = currentHash;
  }
}

function check(value: CrmSegmentRuleFilters) {
  checkChanges(value);
}

const checkDebounced = _.debounce(check, 500);

function close() {
  isVisible.value = false;
  emit('close');
}

async function open() {
  isVisible.value = true;
  checkDebounced;
  await getSegment();
  additionalFilters.value = _.cloneDeep(props.activityFilters);
  lastHash.value = hash(clearFilter(), { algorithm: 'md5' });
}

function addFilter() {
  additionalFilters.value.push(getDefaultRule());
}

function deleteFilter(index: number) {
  additionalFilters.value.splice(index, 1);
}

async function getSegment() {
  isLoading.value = true;
  isSegmentInfoLoading.value = true;

  try {
    if (props.isLocalSegment && props.localSegment) {
      segment.value = _.cloneDeep(props.localSegment);
    } else {
      const { data: { payload, misc } } = await api.getSegment(props.segmentId);
      segment.value = payload as SegmentViewState;
      permissions.value = misc?.permissions || {};
    }

    await getStats(props.state || '');
  } finally {
    isLoading.value = false;
    isSegmentInfoLoading.value = false;
  }
}

async function getStats(stateValue: string = '') {
  const isEdit = stateValue === 'edit' || stateValue === 'rework-edit';

  try {
    statsLoadingCount.value += 1;
    if (!isEdit && !_.isEmpty(props.activity)) {
      const { data: { payload } } = await api.getActivityStatsById(String(props.activity.id || ''));
      stats.value = payload;
    } else if (isEdit && !segment.value.is_global) {
      const segmentRules = clearSegment(segment.value);
      const { data: { payload } } = await api.getSegmentStats({
        ...segmentRules,
        site_id: props.currentBrand,
      } as any);
      stats.value = payload;
    } else {
      const preparedFilters = clearFilter();
      const { data: { payload } } = await api.getSegmentStatsById(
        props.segmentId,
        preparedFilters as CrmSegmentByIdStatsFilters,
      );
      stats.value = payload;
    }
  } finally {
    statsLoadingCount.value -= 1;
  }
}

function clearFilter(value: CrmSegmentRuleFilters = additionalFilters.value) {
  let filterCopy = _.cloneDeep(value);
  filterCopy = filterCopy
    .filter((item) => item.values !== '' && (!Array.isArray(item.values) || item.values.length !== 0))
    .map((item) => ({
      ...item,
      values: item.rule_type === 'player_affiliate_id' || item.rule_type === 'player_sub_affiliate_id'
        ? (Array.isArray(item.values) ? item.values : []).map((entry) => entry?.id)
        : item.values,
    }));
  return filterCopy;
}

function getPlayers() {
  const interval = setInterval(async () => {
    if (props.segmentId) {
      const { data: { payload } } = await api.getSegment(props.segmentId);

      if (payload.is_players_attached) {
        segment.value.is_players_attached = payload.is_players_attached;
        segment.value.players_count = payload.players_count;
        clearInterval(interval);
      }
    }

    if (!props.segmentId) {
      clearInterval(interval);
    }
  }, 15000);
}

async function exportData() {
  const file = _.isEmpty(props.activity)
    ? await api.getSegmentPlayers(props.segmentId)
    : await api.getActivityPlayers(String(props.activity.id || ''));

  const linkBlob = new Blob([file.data], { type: file.headers['content-type'] });
  saveBlob(linkBlob, segment.value.name || '');
}

function handleFilterLoading(value: boolean) {
  filterLoading.value += value ? 1 : -1;
}

function getComments() {
  return api.getSegmentComments(props.segmentId);
}

function postComment(comment: PutCommentsByIdData['body']) {
  return api.postSegmentComments(props.segmentId, comment);
}

async function changeStatus(nextStatus: string) {
  try {
    isLoading.value = true;
    await api.postSegmentChangeStatus(props.segmentId, nextStatus);
    close();
  } finally {
    isLoading.value = false;
  }
}

function handleEdit() {
  emit('edit', props.segmentId);
  isVisible.value = false;
}

function handleSave() {
  emit('update:activity-filters', additionalFilters.value);
  if (!_.isEmpty(stats.value)) {
    emit('updateFilterStats', stats.value);
  }
  emit('close');
}
//#endregion

defineExpose({
  open,
  close,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    :width="1140"
    class="view-segment-popup"
    @close="close"
  >
    <template v-slot:title>
      <div class="t:flex t:items-center t:space-x-8">
        <UiLabel
          :label="segment.name"
          :font-size="20"
          truncate
        />
        <UiSeparator
          type="dot"
          color="--background-on-surface"
        />
        <UiStatusText
          class="view-segment-popup__status"
          :font-size="14"
          :type="detectCrmStatus(segment)"
          :text="$t(`crm.fields.computed_status.values.${detectCrmStatus(segment)}`)"
          bold
        />
        <UiSeparator
          type="dot"
          color="--background-on-surface"
        />
        <CrmTag
          :icon="statusIcon[segment.type]"
          preset="contrast"
          size="large"
        >
          {{ $t(`crm.segments.type.${segment.type}`) }}
        </CrmTag>
      </div>
    </template>
    <template v-slot:body>
      <div class="t:flex t:gap-32">
        <div class="view-segment-popup__block">
          <div
            v-loading="isSegmentInfoLoading || filterLoading !== 0"
            class="t:space-y-16"
          >
            <div>
              <CardTitle
                :title="$t('crm.segments.using_activities')"
              />
              <div
                :class="[
                  'view-segment-popup__activities-list',
                  {'view-segment-popup__activities-list_empty': !usedInActivities}
                ]"
              >
                {{ usedInActivities || $t('crm.segments.activities_empty') }}
              </div>
            </div>
            <div
              v-if="segment.type !== 'manual'"
              class="t:space-y-16"
            >
              <UiLabel
                :label="$t('crm.segments.configure.title_view')"
                :font-size="16"
                bold
              />
              <Tree
                type="outer"
              >
                <TreeBranch
                  v-if="rulesGroup.rules.length > 1"
                >
                  <div class="view-segment-popup__relation">
                    <div class="view-segment-popup__relation-value">
                      {{ $t(`crm.segments.logical_relations.${rulesGroup.logical_relations}`) }}
                    </div>
                  </div>
                </TreeBranch>
                <TreeBranch
                  v-for="(group, id) in rulesGroup.rules"
                  :key="id"
                  :class="{'t:pt-12': (rulesGroup.rules.length > 1 || id !== 0)}"
                >
                  <FormRow>
                    <TreeBranch
                      v-if="group.rules.length > 1"
                      class="view-segment-popup__rule-tree-branch"
                    >
                      <div class="view-segment-popup__relation">
                        <div class="view-segment-popup__relation-value">
                          {{ $t(`crm.segments.logical_relations.${group.logical_relations}`) }}
                        </div>
                      </div>
                    </TreeBranch>
                    <TreeBranch
                      v-for="(group_rule, group_rule_id) in group.rules"
                      :key="group_rule_id"
                      class="view-segment-popup__rule-tree-branch"
                    >
                      <FilterComponent
                        mode="view"
                        :group-rule="group_rule"
                        :available-filters="availableFilters"
                        :current-brand="currentBrand"
                        @loading="handleFilterLoading"
                      />
                    </TreeBranch>
                  </FormRow>
                </TreeBranch>
              </Tree>
            </div>
            <div
              v-if="(showFiltersMode === 'preview' && additionalFilters.length > 0) || showFiltersMode === 'edit'"
              class="t:space-y-16"
            >
              <div class="t:flex t:justify-between">
                <UiLabel
                  :label="$t('crm.segments.activity_filters.title')"
                  :font-size="16"
                  bold
                />
                <div class="view-segment-popup__hint">
                  <UiIcon
                    color="inherit"
                    name="exclamation-circle"
                  />
                  {{ $t('crm.segments.activity_filters.hint') }}
                </div>
              </div>
              <FormRow>
                <div
                  :class="{'t:space-y-8 t:flex t:items-start t:flex-col': showFiltersMode === 'preview' && additionalFilters.length > 1}"
                >
                  <FilterComponent
                    v-for="(groupRule, index) of additionalFilters"
                    :key="index"
                    class="view-segment-popup__filter-item"
                    :available-filters="availableFilters"
                    :group-rule="groupRule"
                    :current-brand="currentBrand"
                    :mode="showFiltersMode === 'edit' ? 'create' : 'view'"
                    @loading="handleFilterLoading"
                    @delete-filter="deleteFilter(index)"
                  />
                </div>

                <UiButton
                  v-if="showFiltersMode === 'edit'"
                  icon="plus"
                  type="positive"
                  no-border
                  :class="{'t:mt-12': additionalFilters.length > 0}"
                  @click="addFilter"
                >
                  {{ $t('crm.segments.configure.add_new_rule') }}
                </UiButton>
              </FormRow>
            </div>
          </div>
          <Comments
            v-if="segment.id !== '' && !isLocalSegment"
            class="t:mt-16"
            :is-editable="canEdit"
            :get-method="getComments"
            :post-method="postComment"
          />
        </div>
        <SegmentInfo
          class="view-segment-popup__block view-segment-popup__block_side"
          :stats="stats"
          :stats-loader="isStatsLoading"
          :player-attached="calcPlayerAttached"
          :moderation-status="segmentInfoModerationStatus"
          :activity-segment-type="activitySegmentTypeValue"
          :show-filters-mode="showFiltersModeValue"
          segment-type="view"
          @export-data="exportData"
          @refresh-stats="getStats(state)"
        />
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:flex t:justify-end t:space-x-8">
        <UiButton
          v-if="status === 'on_review' && showFiltersMode === 'none' && canReview"
          type="negative"
          icon="times"
          :disabled="!isActive"
          @click="changeStatus('decline')"
        >
          {{ $t('crm.buttons.decline') }}
        </UiButton>
        <UiButton
          v-if="status === 'on_review' && showFiltersMode === 'none' && canReview"
          icon="sync-alt"
          :disabled="!isActive"
          @click="changeStatus('for-rework')"
        >
          {{ $t('crm.buttons.for_rework') }}
        </UiButton>
        <UiButton
          v-if="status === 'on_review' && showFiltersMode === 'none' && canReview"
          filled
          icon="check"
          :disabled="!isActive"
          @click="changeStatus('approve')"
        >
          {{ $t('crm.buttons.approve') }}
        </UiButton>
        <UiButton
          v-if="showEdit && ((status === 'draft' || status === 'for_rework') && canEdit)"
          icon="check-double"
          :disabled="!isActive"
          @click="changeStatus('to-review')"
        >
          {{ $t('crm.buttons.review') }}
        </UiButton>
        <template v-if="showFiltersMode === 'edit'">
          <UiButton
            type="negative"
            :disabled="!isActive"
            icon="times"
            @click="close"
          >
            {{ $t('crm.buttons.cancel') }}
          </UiButton>
          <UiButton
            icon="check"
            :disabled="!isActive"
            @click="handleSave"
          >
            {{ $t('crm.buttons.save') }}
          </UiButton>
        </template>
        <UiButton
          v-if="showEdit && ((status === 'draft' || status === 'for_rework') && canEdit)"
          icon="pen"
          :disabled="!isActive"
          @click="handleEdit"
        >
          {{ $t('crm.buttons.edit') }}
        </UiButton>
        <UiButton
          v-if="showFiltersMode !== 'edit' && (status !== 'on_review' || (status === 'on_review' && !canReview))"
          filled
          icon="check"
          :disabled="!isActive"
          @click="close"
        >
          {{ $t('crm.buttons.close') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>

<style lang="scss">
.view-segment-popup {
  &__activities-list {
    padding: 8px;
    font-size: 14px;
    line-height: 18px;
    &_empty {
      color: var(--text-body-secondary);
    }
  }

  &__rule-tree-branch {
    padding-bottom: 8px;

    &:last-of-type {
      padding-bottom: 0;
    }
  }
  &__hint {
    display: flex;
    align-items: center;
    gap: 4px;
    color: var(--text-body-tertiary);
    font-size: 12px;
  }
  &__relation{
    padding: 0;
    width: 46px;
    text-align: center;
    border-radius: 5px;
    background-color: var(--background-primary);
    box-sizing: border-box;
    &-value{
      padding: 6px 12px;
      color: var(--text-body-invert);
      font-size: 12px;
      line-height: normal;
    }
  }
  &__block {
    width: 100%;
    &_side {
      flex-shrink: 0;
      width: 270px;
    }
  }
}
</style>
